/* eslint-disable import/prefer-default-export */
import moment from 'moment';
import { LINE_ITEM_TYPES } from '../constants/manualInvoiceConstants';
import {
  TManualInvoiceBundleV2Payload,
  TManualInvoiceItemPayload,
  TManualInvoiceLineItem,
  TManualInvoicePayload,
  TManualInvoiceSubmitValues,
} from '../types';

// One builder per payload shape, and there are two of them for a domain reason, not a
// cosmetic one: a plain line addresses a single price list by position (price_idx),
// while a bundle has no single list to index — it is a combo of member add-ons, each
// with its own tiers, so the tier is addressed by the ad-spend value (price_value_from)
// and the server resolves it per add-on.
const toItemPayload = ({
  planId,
  bundleId,
  description,
  duration,
  amount,
  discount,
  quantity,
  priceIdx,
}: TManualInvoiceLineItem): TManualInvoiceItemPayload => ({
  id: planId || bundleId || '',
  description,
  duration,
  price: amount,
  discount: discount || 0,
  quantity,
  price_idx: priceIdx ?? -1,
});

const toBundleV2Payload = ({
  bundleV2Id,
  description,
  duration,
  discount,
  priceValueFrom,
}: TManualInvoiceLineItem): TManualInvoiceBundleV2Payload => {
  const payload: TManualInvoiceBundleV2Payload = {
    id: bundleV2Id || '',
    description,
    duration,
    discount: discount || 0,
  };

  // Omitted, not zeroed: absent means "price by the client's own ad spend",
  // while 0 is a real band starting at zero.
  if (priceValueFrom !== null && priceValueFrom !== undefined) {
    payload.price_value_from = priceValueFrom;
  }

  return payload;
};

const buildItems = (lineItems: TManualInvoiceLineItem[]): TManualInvoicePayload['items'] => {
  const items: TManualInvoicePayload['items'] = {};

  lineItems.forEach((lineItem) => {
    switch (lineItem.type) {
      case LINE_ITEM_TYPES.SUBSCRIPTION:
        items.plan = toItemPayload(lineItem);
        break;

      case LINE_ITEM_TYPES.BUNDLE:
      case LINE_ITEM_TYPES.SEATS:
        items.bundles = [...(items.bundles || []), toItemPayload(lineItem)];
        break;

      case LINE_ITEM_TYPES.BUNDLE_V2:
        items.bundle_v2 = toBundleV2Payload(lineItem);
        break;

      case LINE_ITEM_TYPES.CUSTOM:
        items.custom_non_recurrings = [
          ...(items.custom_non_recurrings || []),
          toItemPayload(lineItem),
        ];
        break;

      default:
        break;
    }
  });

  return items;
};

export const transformFormDataToApiRequest = ({
  userId,
  status,
  dueDate,
  subscriptionStartDate,
  lineItems,
}: TManualInvoiceSubmitValues): TManualInvoicePayload => {
  const payload: TManualInvoicePayload = {
    user_id: userId,
    status,
    due_date: dueDate.toISOString(),
    items: buildItems(lineItems),
  };

  // Send the picked calendar day as-is (YYYY-MM-DD), without any timezone shift —
  // toISOString()/utc() would move the day for admins east of UTC.
  if (subscriptionStartDate) {
    payload.subscription_start_date = moment(subscriptionStartDate).format('YYYY-MM-DD');
  }

  return payload;
};
